import { useCallback, useEffect, useState } from 'react'

import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { api } from '@/shared/lib/api'
import { getBonusErrorMessage } from '@/features/bonus/lib/bonusErrorMessages'
import { getApiBasePath, getRedemptionPath } from '@/features/bonus/config'
import type { BonusUnavailableReason } from '@/features/bonus/lib/bonusRedemptionState'
import { alert } from '@/shared/ui/dialog'

export type BonusRecord = {
  id: number
  uid: string
  name: string
  coins_amount: number
  redeemable: boolean
  unavailable_reason?: BonusUnavailableReason | null
  partner_id?: number | null
}

type RedeemState = 'idle' | 'loading' | 'success'

type UseBonusRedemptionOptions = {
  enabled?: boolean
  partnerId?: number | null
}

export const useBonusRedemption = (
  uid: string,
  { enabled = true, partnerId = null }: UseBonusRedemptionOptions = {}
) => {
  const { digitalProductId } = useDigitalProductContext()
  const [bonus, setBonus] = useState<BonusRecord | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [redeemState, setRedeemState] = useState<RedeemState>('idle')
  const [creditedCoins, setCreditedCoins] = useState<number | null>(null)

  // Identifica "qual bônus, em qual sessão, estamos buscando agora". Quando
  // ela muda, ajustamos isLoading/bonus aqui — SÍNCRONO durante o render
  // (padrão React de "adjust state while rendering", react.dev/reference/
  // react/useState#storing-information-from-previous-renders) — em vez de só
  // no useEffect abaixo. Um `useEffect` só comita no PRÓXIMO ciclo de
  // effects; entre o render em que `enabled` vira `true` e esse ciclo
  // rodar, o consumidor (`BonusSheetWatcher`) já re-renderiza vendo
  // `isLoading: false` e `bonus: null` — os valores de quando o sheet
  // estava fechado — e conclui, por um frame inteiro, que o bônus não foi
  // encontrado. Ajustar aqui fecha essa janela: `isLoading` já nasce
  // correto no mesmo render em que `enabled` muda, antes de qualquer efeito
  // rodar.
  const requestKey = enabled && uid ? `${uid}:${partnerId ?? ''}` : null
  const [trackedKey, setTrackedKey] = useState<string | null>(null)
  if (requestKey !== trackedKey) {
    setTrackedKey(requestKey)
    setIsLoading(Boolean(requestKey))
    if (!requestKey) {
      setBonus(null)
      setRedeemState('idle')
      setCreditedCoins(null)
    }
  }

  const loadBonus = useCallback(async () => {
    if (!digitalProductId || !uid) return

    setIsLoading(true)
    try {
      const params =
        partnerId != null ? { partner_id: partnerId } : undefined
      const { data } = await api.get<{ record: BonusRecord }>(
        `${getApiBasePath(digitalProductId)}/${uid}`,
        { params }
      )
      setBonus(data.record)
    } catch {
      setBonus(null)
    } finally {
      setIsLoading(false)
    }
  }, [digitalProductId, partnerId, uid])

  useEffect(() => {
    if (!requestKey) return

    void loadBonus()
  }, [requestKey, loadBonus])

  const redeem = useCallback(async () => {
    if (!digitalProductId || !uid || redeemState === 'loading') return false

    setRedeemState('loading')

    try {
      const payload: { uid: string; partner_id?: number } = { uid }
      if (partnerId != null) payload.partner_id = partnerId

      await api.post<{ record: { bonus_id: number } }>(
        getRedemptionPath(digitalProductId),
        { record: payload }
      )

      setCreditedCoins(bonus?.coins_amount ?? null)
      setRedeemState('success')
      await loadBonus()
      return true
    } catch (error) {
      const code = (error as { response?: { data?: { errors?: Array<{ code?: string }> } } })
        ?.response?.data?.errors?.[0]?.code

      await alert({
        variant: 'destructive',
        title: 'Não foi possível reservar',
        description: getBonusErrorMessage(code)
      })

      setRedeemState('idle')
      return false
    }
  }, [bonus?.coins_amount, digitalProductId, loadBonus, partnerId, redeemState, uid])

  const resetRedeemState = useCallback(() => {
    setRedeemState('idle')
    setCreditedCoins(null)
  }, [])

  return {
    bonus,
    isLoading,
    redeemState,
    creditedCoins,
    redeem,
    resetRedeemState,
    refetch: loadBonus
  }
}
